/**
 * useBankrollSimulation Hook
 * Runs the Monte Carlo bankroll simulation on demand. Shared between web and mobile.
 */

import { useCallback, useState } from 'react';
import type { HttpClient } from '../api/httpClient';
import type { SimulationResult } from '../bankrollSimulation';

export interface UseBankrollSimulationOptions {
  httpClient: HttpClient;
}

export interface UseBankrollSimulationResult {
  result: SimulationResult | null;
  loading: boolean;
  error: string | null;
  simulate: (startingBankroll: number, days: number) => Promise<void>;
}

export function useBankrollSimulation({
  httpClient,
}: UseBankrollSimulationOptions): UseBankrollSimulationResult {
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const simulate = useCallback(
    async (startingBankroll: number, days: number) => {
      setLoading(true);
      setError(null);
      try {
        setResult(
          await httpClient.post<SimulationResult>('/api/ml/simulate-bankroll', {
            startingBankroll,
            days,
          }),
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Simulation failed');
      } finally {
        setLoading(false);
      }
    },
    [httpClient],
  );

  return { result, loading, error, simulate };
}
